import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CampaignStep, CampaignStepId } from './campaignSteps';

export interface CampaignStepRailProps {
  steps: CampaignStep[];
  active: CampaignStepId;
  onSelect: (step: CampaignStepId) => void;
  /** The active step's panel. */
  children: ReactNode;
  /** A phone: the steps stack, the open one holding its panel. */
  stacked: boolean;
  /** Steps marked done, with a tick. */
  done?: Partial<Record<CampaignStepId, boolean>>;
}

/**
 * The steps of a campaign down the side with the open one beside them — or,
 * on a phone, as a stacked list where the open step unfolds in place. The
 * panel is rendered once either way.
 */
export function CampaignStepRail({
  steps,
  active,
  onSelect,
  children,
  stacked,
  done = {},
}: CampaignStepRailProps) {
  const stepButton = (step: CampaignStep, index: number) => {
    const Icon = step.icon;
    const isActive = step.id === active;
    return (
      <button
        type="button"
        onClick={() => onSelect(step.id)}
        aria-current={isActive ? 'step' : undefined}
        aria-expanded={stacked ? isActive : undefined}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
          isActive ? 'bg-primary-50 text-primary-800' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            isActive
              ? 'bg-primary-600 text-white'
              : done[step.id]
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
          }`}
          aria-hidden
        >
          {done[step.id] && !isActive ? '✓' : index + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {step.label}
          </span>
          <span className="block truncate text-xs text-gray-500">{step.hint}</span>
        </span>
        {stacked && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isActive ? 'rotate-180' : ''}`}
            aria-hidden
          />
        )}
      </button>
    );
  };

  if (stacked) {
    return (
      <ol className="space-y-2" aria-label="Campaign steps">
        {steps.map((step, index) => (
          <li key={step.id} className="rounded-2xl border border-gray-200 bg-white p-1">
            {stepButton(step, index)}
            {step.id === active && <div className="px-2 pb-3 pt-2">{children}</div>}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className="grid grid-cols-[15rem_minmax(0,1fr)] gap-6 items-start">
      <nav aria-label="Campaign steps" className="sticky top-24">
        <ol className="space-y-1">
          {steps.map((step, index) => (
            <li key={step.id}>{stepButton(step, index)}</li>
          ))}
        </ol>
      </nav>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
